import { Button, Modal, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconMessage, IconThumbUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { FeedItemComments } from 'src/Components/Comments';
import UserAvatar from 'src/Components/NavBar/UserAvatar';
import { ShareToWhatsAppButton } from 'src/Components/Shared/ShareToWhatsAppButton';
import { useCreateCommunityFeedItemLike } from 'src/Hooks/Mutations/useCreateCommunityFeedItemLike';
import { useDeleteCommunityFeedItemLike } from 'src/Hooks/Mutations/useDeleteCommunityFeedItemLike';

import styles from './CommunityFeedItemSocialInteractions.module.scss';
import { Props } from './CommunityFeedItemSocialInteractions.props';
import { LikesListModal } from './LikesListModal';

// t('likeCount_one', '{{count}} J’aime');
// t('likeCount_many', '{{count}} J’aimes');
// t('likeCount_other', '{{count}} J’aimes');
// t('commentCount_one', '{{count}} Commentaire');
// t('commentCount_many', '{{count}} Commentaires');
// t('commentCount_other', '{{count}} Commentaires');

const CommunityFeedItemSocialInteractions = ({
  feedItem,
  areCommentsOpenByDefault,
  grow,
  highlightedCommentId,
  excerpt,
}: Props) => {
  const { t } = useTranslation();

  const [commentsVisibility, { toggle: toggleCommentsVisibility }] =
    useDisclosure(areCommentsOpenByDefault);

  const [likesListModalOpen, { open: openLikesList, close: closeLikesList }] = useDisclosure(false);

  const { mutate: createLike, isLoading: loadingCreate } = useCreateCommunityFeedItemLike();
  const { mutate: deleteLike, isLoading: loadingDelete } = useDeleteCommunityFeedItemLike();
  const loading = loadingCreate || loadingDelete;

  const toggleLike = () => {
    if (loading) return;

    if (feedItem.likes.didILike) {
      deleteLike(feedItem.id);
    } else {
      createLike(feedItem.id);
    }
  };

  const likeList = feedItem.likes.firstFive.map(like => (
    <Tooltip key={like.id} label={like.from.firstName + ' ' + like.from.lastName}>
      <div className={styles.likesListAvatar}>
        <UserAvatar size={30} user={like.from} opaque />
      </div>
    </Tooltip>
  ));

  return (
    <>
      <Modal
        opened={likesListModalOpen}
        onClose={closeLikesList}
        title={t('likes.likesList', 'Liste des réactions')}
      >
        <LikesListModal feedItem={feedItem} onClose={closeLikesList} />
      </Modal>

      <div className={styles.separator} />

      <div className={styles.interactionsRow}>
        <div className={styles.likesList} onClick={openLikesList}>
          {likeList}
        </div>

        <div className={styles.interactionsActions}>
          <Button
            onClick={toggleLike}
            className={[
              styles.likeButton,
              feedItem.likes.didILike ? styles.isAlreadyLiked : '',
            ].join(' ')}
            leftSection={<IconThumbUp size={16} />}
            loading={loading}
          >
            {t('likes.likeCount', { count: feedItem.likes.count })}
          </Button>

          <ShareToWhatsAppButton
            assetName={feedItem.assets[0]?.name ?? ''}
            excerpt={excerpt}
            target={{ kind: 'feedPost', postId: feedItem.id }}
            trackingSource={'community_feed'}
          />

          {!areCommentsOpenByDefault ? (
            <Button
              onClick={toggleCommentsVisibility}
              className={[
                styles.commentButton,
                commentsVisibility ? styles.commentButtonEnabled : '',
              ].join(' ')}
              leftSection={<IconMessage size={16} />}
            >
              {feedItem.comments.count > 0
                ? t('likes.commentCount', { count: feedItem.comments.count })
                : t('community.reply', 'Répondre')}
            </Button>
          ) : null}
        </div>
      </div>

      {commentsVisibility ? (
        <FeedItemComments
          feedItem={feedItem}
          grow={grow}
          highlightedCommentId={highlightedCommentId}
        />
      ) : null}
    </>
  );
};

export default CommunityFeedItemSocialInteractions;
